import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AdaptiveFilter,
  Box,
  CalendarIcon,
  CalendarPickerIcon,
  DateRangeDropdown,
  FlexList,
  List,
  ListItemBase,
  OptionKey,
  RawButton,
  SearchInput,
  SelectDropdown,
  Text,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import { ALL_OPTION } from '@/common/constants';
import ROUTES from '@/common/routes';
import { FlexListWrapper } from '@/components/wrappers';
import { useHistory } from '@/modules/core/hooks';
import { useStore } from '@/modules/transaction/components/Contributions/store/useSuccessContributionModal.store';
import PaginationBarWithMediaQuery from '@/modules/transaction/components/PaginationBar';
import { EmptyTransactionsListPlaceholder } from '@/modules/transaction/components/TransactionsList/EmptyTransactionsListPlaceholder';
import { DropdownInputWrapper } from '@/modules/transaction/components/TransactionsList/TransactionsList.styles';

import TransactionItem from '../../TransactionItem';

import useGetTransactionTypesByAccountIdQuery from './queries/useGetTransactionTypesByAccountId.query';
import { useActivitiesDatesRange, useActivitiesList } from './hooks';
import { ProcessingActivitiesList } from './ProcessingActivitiesList';

const DEFAULT_PAGE_SIZE = 10;

const HEADERS: ListItemBase[] = [
  {
    key: 'provider',
    title: '',
    flex: 5.3,
  },
  {
    key: 'status',
    title: '',
    flex: 3,
  },
  {
    key: 'amount',
    title: 'Amount',
    flex: 2,
  },
  {
    key: 'statusDate',
    title: 'Date',
    flex: 0.7,
    endAlign: true,
  },
  {
    key: 'option',
    title: '',
    flex: 0.5,
  },
];

const stateAllToggling = (statuses: OptionKey[]) => {
  const minStatusesLength = 2;
  const isStatusAllSelected = statuses[statuses.length - 1] === ALL_OPTION.value;

  if (statuses.length === 0 || isStatusAllSelected) {
    return [ALL_OPTION.value];
  }

  const isStatusAllChangedToAnother =
    statuses.length === minStatusesLength && statuses[0] === ALL_OPTION.value;

  if (isStatusAllChangedToAnother) {
    return statuses.filter((status) => status !== ALL_OPTION.value);
  }

  return statuses;
};

interface Props {
  hideHeader?: boolean;
  accountId: string;
  disabled?: boolean;
  title?: string;
  hideBackground?: boolean;
}
const ActivitiesList: React.FC<Props> = ({ accountId, title, hideHeader, disabled, hideBackground }) => {
  const { transactionTypeOptions, isLoading: areTransactionTypesLoading } =
    useGetTransactionTypesByAccountIdQuery(accountId);
  const typesOptions = useMemo(() => [ALL_OPTION, ...transactionTypeOptions], [transactionTypeOptions]);
  const [types, setTypes] = useState<OptionKey[]>([ALL_OPTION.value]);
  const history = useHistory();
  const [initialData, setInitialData] = useState(false);
  const { onChangeDates, formatedDates } = useActivitiesDatesRange();

  const typesValues = useMemo(() => stateAllToggling(types), [types]);

  const setSuccessContributionAction = useStore((store) => store.handleSetAdditionalAction);

  const hasTypes = useMemo(
    () => typesValues.length > 0 && !typesValues.some((type) => type === ALL_OPTION.value),
    [typesValues]
  );

  const { t } = useTranslation();
  const { page, setPage, setPerPage, perPage, searchString, setSearchString, currentSearch } =
    usePaginationParams(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    setPage(1);
  }, [setPage, currentSearch, types, formatedDates.length]);

  const { isLoading, total, data, refetch } = useActivitiesList({
    accountId,
    page: page - 1,
    perPage,
    searchString: currentSearch || '',
    ...(hasTypes ? { types: typesValues } : {}),
    startDate: formatedDates[0],
    endDate: formatedDates[1],
    hasActiveHold: false,
  });

  const isAnyInputFilled = useMemo(
    () => searchString.length || types.length || formatedDates.length,
    [formatedDates.length, types.length, searchString.length]
  );
  const isDataInclude = useMemo(() => !!data.length, [data]);
  const isInitialData = useCallback(
    () => (!data.length && !isAnyInputFilled ? setInitialData(false) : setInitialData(true)),
    [data.length, isAnyInputFilled]
  );
  useEffect(() => {
    isInitialData();
  }, [isInitialData]);

  useEffect(() => {
    setSuccessContributionAction(() => refetch());
  }, [refetch, setSuccessContributionAction]);
  const { isMobile } = useResponsive();
  const currentOnPick = useCallback(
    (claimId?: number, postingId?: number | string) => {
      if (claimId) {
        return history.push(ROUTES.TRANSACTION_DETAILS_CLAIM(accountId, claimId));
      }
      if (postingId) {
        return history.push(ROUTES.TRANSACTION_DETAILS_POSTING(accountId, postingId));
      }
      return null;
    },
    [accountId, history]
  );

  const {
    isFilterVisible,
    setIsFilterVisible,
    handleCancelFilter,
    handleSubmitFilter,
    isFilterApplied,
    handleResetFilter,
    filters,
    setFilter,
  } = AdaptiveFilter.useAdaptiveFilter<{
    types: OptionKey[];
    dates: [Date | null, Date | null];
  }>({
    initialFilters: {
      types: [ALL_OPTION.value],
      dates: [null, null],
    },
    onSubmit: (appliedFilters) => {
      setTypes(appliedFilters.types);
      onChangeDates(appliedFilters.dates);
    },
  });

  return (
    <Box>
      <ProcessingActivitiesList accountId={accountId} />

      <Box
        direction="column"
        data-testid="TransactionsList"
        background={hideBackground ? 'transparent' : 'module'}
        round={hideBackground ? undefined : 'moduleRound'}
        pad={{ bottom: initialData ? 'spacing12' : 'spacing24' }}
      >
        <Box
          direction={isMobile ? 'column' : 'row'}
          data-testid={`activities-list-${initialData}`}
          align="center"
          margin={{ bottom: isMobile ? 'spacing12' : 'spacing4' }}
          pad={{
            horizontal: hideBackground ? undefined : 'spacing24',
            top: hideBackground ? undefined : 'spacing24',
          }}
        >
          <Box direction="row" align="center" fill>
            <Box direction="row" align="center">
              <Text size="large" color="blockTitle" weight="bold" textAlign="center">
                {title || t('Expenses')}
              </Text>
            </Box>

            <Box margin={{ left: 'auto' }}>
              {!isMobile && (
                <SearchInput
                  disabled={!initialData || disabled}
                  value={searchString}
                  onChange={setSearchString}
                  placeholder={t('Search')}
                  hasSearchingHistory
                />
              )}
            </Box>
            {isMobile ? (
              <Box height={{ min: '48px' }} align="center" justify="center" width={{ min: '48px' }}>
                <AdaptiveFilter
                  isFilterApplied={isFilterApplied}
                  testId="test-filter"
                  title="Title"
                  onClear={handleResetFilter}
                  isVisible={isFilterVisible}
                  onSetIsVisible={setIsFilterVisible}
                  footer={
                    <AdaptiveFilter.Footer
                      testId="footer"
                      onCancel={handleCancelFilter}
                      onSubmit={handleSubmitFilter}
                    />
                  }
                >
                  <AdaptiveFilter.Select
                    testId="activity"
                    isAllMode
                    label="Type"
                    options={transactionTypeOptions}
                    value={filters.types}
                    onChangeValue={(value) => setFilter('types', value)}
                  />

                  <AdaptiveFilter.DateRange
                    testId="period"
                    label="Period"
                    value={filters.dates}
                    onChangeValue={(value) => setFilter('dates', value)}
                  />
                </AdaptiveFilter>
              </Box>
            ) : (
              <>
                <DropdownInputWrapper>
                  <SelectDropdown
                    disabled={!initialData || disabled}
                    id="type"
                    allMode
                    name={t('Activity type')}
                    prefix="Type"
                    options={typesOptions}
                    values={typesValues}
                    onChangeValues={setTypes}
                  />
                </DropdownInputWrapper>
                <DropdownInputWrapper>
                  <DateRangeDropdown
                    ellipsisMode
                    disabled={!initialData || disabled}
                    prefix={t('Period:')}
                    className="period-picker"
                    onChange={onChangeDates}
                    startDate={formatedDates[0]}
                    endDate={formatedDates[1]}
                    icon={
                      <Box margin={{ bottom: !initialData ? 'spacing4' : undefined }}>
                        {!initialData ? (
                          <CalendarPickerIcon />
                        ) : (
                          <CalendarIcon color="iconPrimary" size="16px" />
                        )}
                      </Box>
                    }
                  />
                </DropdownInputWrapper>
              </>
            )}
          </Box>
          {isMobile && (
            <Box fill margin={{ top: 'spacing12' }}>
              <SearchInput
                disabled={!initialData || disabled}
                value={searchString}
                onChange={setSearchString}
                placeholder={t('Search')}
                hasSearchingHistory
              />
            </Box>
          )}
        </Box>
        {isMobile && (
          <Box pad={{ horizontal: hideBackground ? undefined : 'l' }} direction="row" justify="between">
            <Text size="large" weight={700}>
              {`${t('Results:')} ${total || 0}`}
            </Text>
            {isFilterApplied && (
              <RawButton onClick={handleResetFilter}>
                <Text size="large" weight={700} color="buttonPrimary">
                  {t('Clear All')}
                </Text>
              </RawButton>
            )}
          </Box>
        )}
        <FlexListWrapper>
          <FlexList
            testId="activities_list"
            headers={isMobile ? [] : initialData && !hideHeader ? HEADERS : []}
            rows={[]}
            total={total}
            moreCount={perPage}
            pad={{
              horizontal: hideBackground ? '0px' : isMobile ? 'spacing16' : 'spacing24',
              top: 'spacing12',
              bottom: 'spacing24',
            }}
            loading={isLoading || areTransactionTypesLoading}
            footer={
              isLoading || areTransactionTypesLoading ? (
                <List.Preloader count={4} />
              ) : (
                <Box direction="column">
                  {!isDataInclude && <EmptyTransactionsListPlaceholder title="Activities" />}

                  {data.map((item) => (
                    <TransactionItem
                      key={item?.postingId || item?.claimId}
                      category={item.category}
                      type={item?.transactionType}
                      title={item.title}
                      amount={item.amount}
                      status={item.status}
                      date={item.date}
                      onPick={() =>
                        currentOnPick(item?.claimId, item?.originalFeePostingId || item?.postingId)
                      }
                    />
                  ))}
                  {isDataInclude && (
                    <PaginationBarWithMediaQuery
                      page={page}
                      total={total}
                      pageSize={perPage}
                      onChangePage={setPage}
                      onChangePageSize={setPerPage}
                    />
                  )}
                </Box>
              )
            }
          />
        </FlexListWrapper>
      </Box>
    </Box>
  );
};

export default ActivitiesList;
